import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Link, useLocation, useNavigate } from 'react-router';

import { resendSignUpCode, signIn } from 'aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../reducers/authSlice';
import { IconAlertSquareRounded, IconMail, IconEye, IconEyeOff } from '@tabler/icons-react';

// Import your assets
import logoWhite from '../../assets/images/Logo.png';
// import flatColorIconsGoogle from '../../assets/images/flat-color-icons_google.png';
import awsconfig from '../../awsconfig';
import ConfirmSignUp from '../Register/ConfirmSignUp/ConfirmSignUp';
import { initializeAuthStore } from '../../utils/authUtils';
import ConfirmLoginNewPassword from './ConfirmLoginNewPassword';
import { sendToKinesis } from '../../analytics/kinesisTransport';
import DOMPurify from 'dompurify';

Amplify.configure({ ...awsconfig });

// Login validation Schema
const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email address field is required'),
  password: Yup.string().required('Password field is required'),
  rememberMe: Yup.boolean(),
});

function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [userCredentials, setUserCredentials] = useState(null);

  const [nextStep, setNextStep] = useState(null);

  const rawError = location.state?.errorDescription;
  const GoogleSignInError = rawError ? DOMPurify.sanitize(rawError) : undefined;

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userAttributes = useSelector((state) => state.auth.userAttributes);

  useEffect(() => {
    if (isAuthenticated) {
      if (userAttributes['custom:role'] === 'super_admin') {
        navigate('/super-admin');
        return;
      }
      navigate('/landing');
    }
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      setUserCredentials({
        username: values.email,
        password: values.password,
        isInitiatedFromLogin: true,
      });
      const { isSignedIn, nextStep } = await signIn({
        username: values.email,
        password: values.password,
      });

      if (
        nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setNextStep(nextStep);
      }

      if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        sendConfirmSignUpCode(values.email);
        setNextStep(nextStep);
      }

      if (isSignedIn) {
        const authData = await initializeAuthStore();

        if (authData) {
          // Update Redux store with the auth data
          dispatch(login(authData));
        } else {
          // Handle case where auth initialization fails
          throw new Error('Failed to initialize authentication');
        }

        sendToKinesis('user_events', 'USER_LOGGED_IN', {
          userid: authData.user.userId,
        });
      }
    } catch (err) {
      // Handle specific AWS Amplify authentication errors
      switch (err.name) {
        case 'UserNotFoundException':
          setStatus('No account exists with this email');
          break;
        case 'NotAuthorizedException':
          setStatus(err.message);
          break;
        case 'UserNotConfirmedException':
          setStatus('Please confirm your account');
          break;
        case 'UserLambdaValidationException':
          setStatus(err.message.split('|')[1]);
          break;
        default:
          setStatus('An unexpected error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sendConfirmSignUpCode = async (username) => {
    try {
      await resendSignUpCode({ username });
    } catch (error) {
      console.error('Failed to resend code:', error);
    }
  };

  if (nextStep?.signInStep === 'CONFIRM_SIGN_UP' && userCredentials) {
    return (
      <div className="container-fluid prelogin-container">
        <div className="row">
          <div className="col-lg-6 col-12 p-0">
            <div className="prelogin-leftside">
              <div className="container fix-height">
                <img className="logo" src={logoWhite} alt="logo white" />
                <div className="text">
                  <p className="fs-5 fs-md-4 fw-normal mb-4">
                    Empowering healthcare providers with real-time prescription
                    coverage clarity — for faster, safer decisions at the point
                    of care.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-12 p-0 bg-light">
            <div className="prelogin-rightside">
              <div className="bg-white p-4 rounded shadow-sm">
                <ConfirmSignUp
                  userCredentials={userCredentials}
                  onGoBack={() => {
                    setNextStep(null);
                    setUserCredentials(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return (
      <ConfirmLoginNewPassword
        onGoBack={() => {
          setNextStep(null);
          setUserCredentials(null);
        }}
      />
    );
  }
  return (
    !isAuthenticated && (
      <div className="prelogin-container w-full">
        <div className="flex flex-col lg:flex-row min-h-screen">
          <div className="w-full lg:w-1/2 p-0">
            <div className="prelogin-leftside">
              <div className="container fix-height">
                <img className="logo-large" src={logoWhite} alt="logo white" />
                <div className="text">
                  <h1 className="tagline-large">
                    Empowering healthcare providers with real-time prescription coverage clarity
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full lg:w-1/2 p-0 bg-white">
            <div className="prelogin-rightside">
              <div className="p-5">
                <Formik
                  initialValues={{
                    email: '',
                    password: '',
                    rememberMe: false,
                    showPassword: false,
                  }}
                  initialStatus={GoogleSignInError}
                  validationSchema={LoginSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    errors,
                    touched,
                    status,
                    isSubmitting,
                    setFieldValue,
                    values,
                  }) => (
                    <>
                      <Form className="cst-form">
                        <h2 className="text-2xl font-bold mb-2 text-center">
                          Login to Your Account
                        </h2>
                        <p className="text-center text-muted-foreground text-sm mb-4">
                          Welcome back! Please enter your credentials.
                        </p>

                        <div className="mb-4">
                          <label
                            htmlFor="email"
                            className="block pb-2 text-sm font-semibold"
                          >
                            Email Address
                          </label>
                          <div className="input-with-icon">
                            <IconMail className="input-icon" size={20} />
                            <Field
                              type="email"
                              name="email"
                              disabled={isSubmitting}
                              id="email"
                              autoComplete="off"
                              className={`form-control pl-12 ${errors.email && touched.email ? 'border-red-500' : ''}`}
                              placeholder="name@example.com"
                            />
                          </div>
                          {errors.email && touched.email && (
                            <div className="text-red-500 text-sm mt-1">
                              {errors.email}
                            </div>
                          )}
                        </div>

                        <div className="mb-2">
                          <label
                            htmlFor="password"
                            className="block pb-2 text-sm font-semibold"
                          >
                            Password
                          </label>
                          <div className="input-with-icon create-pass">
                            <Field
                              type={values.showPassword ? 'text' : 'password'}
                              name="password"
                              disabled={isSubmitting}
                              id="password"
                              autoComplete="off"
                              className={`form-control pr-12 ${errors.password && touched.password ? 'border-red-500' : ''}`}
                              placeholder="Enter your password"
                            />
                            {values.password && (
                              <span
                                className="password-toggle-icon"
                                role="button"
                                disabled={isSubmitting}
                                onClick={() => {
                                  if (!isSubmitting) {
                                    setFieldValue(
                                      'showPassword',
                                      !values.showPassword,
                                    );
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                {values.showPassword ? (
                                  <IconEyeOff size={20} />
                                ) : (
                                  <IconEye size={20} />
                                )}
                              </span>
                            )}
                          </div>
                          {errors.password && touched.password && (
                            <div className="text-red-500 text-sm mt-1">
                              {errors.password}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between mb-4" style={{ marginTop: '0.25rem' }}>
                          <div className="flex items-center gap-2">
                            <Field
                              type="checkbox"
                              name="rememberMe"
                              disabled={isSubmitting}
                              className="cursor-pointer w-4 h-4 rounded-md"
                              id="rememberMe"
                            />
                            <label
                              className="text-sm cursor-pointer"
                              htmlFor="rememberMe"
                            >
                              Remember me
                            </label>
                          </div>
                          <Link
                            to="/reset-password"
                            onClick={(e) => isSubmitting && e.preventDefault()}
                            aria-disabled={isSubmitting}
                            tabIndex={isSubmitting ? -1 : 0}
                            className="text-primary text-sm no-underline hover:underline forgot-password"
                          >
                            Forgot password?
                          </Link>
                        </div>

                        {status && (
                          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 mb-4 flex items-center gap-2" role="alert">
                            <IconAlertSquareRounded size={20} />
                            <span>{status}</span>
                          </div>
                        )}

                        <button
                          type="submit"
                          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-2.5 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span 
                                className="spinner-border spinner-border-sm me-2" 
                                role="status" 
                                aria-hidden="true"
                              />
                              <span>Logging in...</span>
                            </>
                          ) : (
                            'Login'
                          )}
                        </button>

                        {/* <div className="or-devider">
                    <span>OR</span>
                  </div> */}

                        {/* <button
                    type="button"
                    className="btn register-google-btn"
                    onClick={() => signInWithRedirect({ provider: 'Google' })}
                  >
                    <img
                      className="me-3"
                      src={flatColorIconsGoogle}
                      alt="google icon"
                    />
                    Login with Google
                  </button> */}
                      </Form>
                      <div className="mt-4 text-center">
                        <p className="text-muted-foreground text-sm mb-0">
                          Don't have an account?{' '}
                          <Link
                            to="/register"
                            onClick={(e) => isSubmitting && e.preventDefault()}
                            aria-disabled={isSubmitting}
                            tabIndex={isSubmitting ? -1 : 0}
                            className="text-primary font-medium no-underline hover:underline"
                          >
                            Sign up here
                          </Link>
                        </p>
                      </div>
                    </>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
}

export default Login;
